import { type ReactNode } from "react";

import { BTOverflow, BTText } from "@btnet/core-components";

import { BdsIconX } from "@buildertrend/components";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { BTModal } from "commonComponents/btWrappers/BTModal/BTModal";
import PageTitle from "commonComponents/utilities/PageTitle/PageTitle";

export interface ISpecificationShareModalShellProps {
    testid: string;
    visible: boolean;
    onClose: () => void;
    title: string;
    subtitle: string;
    /**
     * @default "Confirm" - only the Publish flow needs to override this.
     */
    confirmText?: string;
    isSubmitting: boolean;
    confirmDisabled?: boolean;
    onConfirm: () => void;
    children: ReactNode;
}

/**
 * Shared chrome (header + footer) for the Specification publish/share modals -
 * SpecificationPublishModal and MassSpecificationShareUpdateModal render identical
 * title/close/subtitle headers and Cancel/Confirm footers around different body content
 * (single vs. bulk share checkboxes + the notify-recipients picker).
 */
export function SpecificationShareModalShell({
    testid,
    visible,
    onClose,
    title,
    subtitle,
    confirmText = "Confirm",
    isSubmitting,
    confirmDisabled = false,
    onConfirm,
    children,
}: ISpecificationShareModalShellProps) {
    return (
        <BTModal
            data-testid={testid}
            title={undefined}
            visible={visible}
            useModalLayout
            beforeClose={onClose}
            width="480px"
            centered
            removeBodyPadding
        >
            <PageTitle title={title} />
            <div className="flex flex-col w-full">
                <div className="flex flex-col gap-1 py-3 px-6">
                    <div className="flex flex-row justify-between grow items-baseline">
                        <BTText size="distinct-lg">{title}</BTText>
                        <BTButton
                            data-testid="close"
                            onClick={onClose}
                            icon={<BdsIconX />}
                            aria-label={`Close ${title.toLowerCase()} modal`}
                            type="tertiary"
                        />
                    </div>
                    <div className="flex flex-row">
                        <BTText size="normal-md">{subtitle}</BTText>
                    </div>
                </div>
                <div className="flex flex-col gap-3 py-3 px-6">{children}</div>
                <div className="flex flex-row py-4 px-6">
                    <BTOverflow testid={`${testid}-footer`} style={{ justifyContent: "flex-end" }}>
                        <BTOverflow.Item id="cancel">
                            <BTButton
                                data-testid={`${testid}-cancel`}
                                type="secondary"
                                onClick={onClose}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </BTButton>
                        </BTOverflow.Item>
                        <BTOverflow.Item id="confirm" pinned>
                            <BTButton
                                data-testid={`${testid}-confirm`}
                                type="primary"
                                onClick={onConfirm}
                                disabled={confirmDisabled || isSubmitting}
                                loading={isSubmitting}
                            >
                                {confirmText}
                            </BTButton>
                        </BTOverflow.Item>
                    </BTOverflow>
                </div>
            </div>
        </BTModal>
    );
}
